<template>
  <div class="tl">
    <Breadcrumbs main="Пользователи" title="Фейковые пользователи"/>

    <Modal name="DeleteFakeUser" title="Удаление фейкового пользователя" v-model="isShowDeleteFakeUserModal"
           :params="{ id: selectedFakeUserId }"/>
    <Modal name="EditFakeUser" title="Редактирование фейк пользователя" v-model="isShowEditFakeUserModal"
           :params="{ id: selectedFakeUserId }"/>
    <Modal name="CreateFakeUser" title="Создание фейк пользователя" v-model="isShowCreateFakeUserModal"/>

    <div class="container-fluid tl-container">

      <!-- ================== TOOLBAR ================== -->
      <div class="tl-toolbar">
        <div class="tl-toolbar__main">
          <div class="tl-search" :class="{ 'is-busy': isLoadingFakeUsers }">
            <i class="fa fa-search tl-search__icon"></i>
            <input
                ref="search"
                type="search"
                class="tl-search__input"
                v-model="searchModel"
                placeholder="Поиск по ID, username или игровому нику…"
                @keydown.esc.prevent="clearSearch"
            />
            <button v-if="searchModel" type="button" class="tl-search__clear" @click="clearSearch">
              <i class="fa fa-times"></i>
            </button>
            <kbd v-else class="tl-search__kbd">/</kbd>
          </div>

          <b-dropdown variant="link" class="tl-drop" no-caret menu-class="tl-drop__menu">
            <template #button-content>
              <span class="tl-drop__btn" :class="{ 'is-on': refine !== 'all' }">
                <i class="fa fa-gamepad"></i>
                <span class="tl-drop__btn-text">{{ refineLabel }}</span>
                <i class="fa fa-angle-down"></i>
              </span>
            </template>
            <b-dropdown-item @click="refine = 'all'">
              <i class="fa fa-check tl-drop__check" :class="{ 'is-hidden': refine !== 'all' }"></i> Любые профили
            </b-dropdown-item>
            <b-dropdown-item @click="refine = 'pubg'">
              <i class="fa fa-check tl-drop__check" :class="{ 'is-hidden': refine !== 'pubg' }"></i> Только с PUBG
            </b-dropdown-item>
            <b-dropdown-item @click="refine = 'mlbb'">
              <i class="fa fa-check tl-drop__check" :class="{ 'is-hidden': refine !== 'mlbb' }"></i> Только с MLBB
            </b-dropdown-item>
            <b-dropdown-item @click="refine = 'empty'">
              <i class="fa fa-check tl-drop__check" :class="{ 'is-hidden': refine !== 'empty' }"></i> Без игровых профилей
            </b-dropdown-item>
          </b-dropdown>

          <div class="tl-spacer"></div>

          <div class="tl-seg" role="group" aria-label="Вид">
            <button type="button" class="tl-seg__btn" :class="{ 'is-on': viewMode === 'table' }"
                    @click="setViewMode('table')" v-b-tooltip.hover.bottom="'Таблица'">
              <i class="fa fa-list-ul"></i>
            </button>
            <button type="button" class="tl-seg__btn" :class="{ 'is-on': viewMode === 'grid' }"
                    @click="setViewMode('grid')" v-b-tooltip.hover.bottom="'Карточки'">
              <i class="fa fa-th-large"></i>
            </button>
          </div>

          <button type="button" class="tl-icon-btn" :class="{ 'is-on': density === 'compact' }"
                  @click="toggleDensity" v-b-tooltip.hover.bottom="'Плотность строк'">
            <i class="fa fa-bars"></i>
          </button>

          <router-link :to="{ name: 'users_real' }" class="tl-icon-btn"
                       v-b-tooltip.hover.bottom="'Реальные пользователи'">
            <i class="fa fa-users"></i>
          </router-link>

          <button type="button" class="tl-icon-btn" :class="{ 'is-spin': isLoadingFakeUsers }"
                  @click="refresh" v-b-tooltip.hover.bottom="'Обновить'">
            <i class="fa fa-refresh"></i>
          </button>

          <button type="button" class="tl-btn-primary" @click="createFakeUser">
            <i class="fa fa-plus"></i> Создать пользователя
          </button>
        </div>

        <div v-if="hasAnyRefine" class="tl-chips">
          <span class="tl-chips__label">Фильтры:</span>

          <button v-if="searchApplied" type="button" class="tl-chip" @click="clearSearch">
            <i class="fa fa-search"></i> «{{ searchApplied }}» <i class="fa fa-times tl-chip__x"></i>
          </button>

          <button v-if="refine !== 'all'" type="button" class="tl-chip tl-chip--local" @click="refine = 'all'">
            <i class="fa fa-gamepad"></i> {{ refineLabel }}
            <span class="tl-chip__hint">на странице</span>
            <i class="fa fa-times tl-chip__x"></i>
          </button>

          <button type="button" class="tl-chips__reset" @click="resetAll">Сбросить всё</button>
        </div>
      </div>

      <!-- ================== CONTENT ================== -->
      <div class="tl-panel" :class="['tl-panel--' + density]">

        <div v-if="isLoadingFakeUsers" class="tl-skeleton">
          <div v-for="n in 6" :key="'sk' + n" class="tl-skeleton__row">
            <span class="tl-sk tl-sk--avatar"></span>
            <span class="tl-sk tl-sk--wide"></span>
            <span class="tl-sk"></span>
            <span class="tl-sk"></span>
            <span class="tl-sk tl-sk--sm"></span>
          </div>
        </div>

        <div v-else-if="!rows.length" class="tl-empty">
          <div class="tl-empty__art"><i class="fa fa-user-secret"></i></div>
          <div class="tl-empty__title">
            {{ hasAnyRefine ? 'Ничего не нашлось' : 'Фейковых пользователей нет' }}
          </div>
          <div class="tl-empty__text">
            {{ hasAnyRefine
              ? 'Попробуйте изменить условия поиска или сбросить фильтры.'
              : 'Фейковые пользователи нужны, чтобы собирать составы фейковых команд.' }}
          </div>
          <div class="tl-empty__actions">
            <button v-if="hasAnyRefine" type="button" class="tl-btn-ghost-2" @click="resetAll">
              Сбросить фильтры
            </button>
            <button type="button" class="tl-btn-primary" @click="createFakeUser">
              <i class="fa fa-plus"></i> Создать пользователя
            </button>
          </div>
        </div>

        <!-- table -->
        <div v-else-if="viewMode === 'table'" class="tl-table-wrap">
          <b-table
              ref="table"
              class="tl-table"
              responsive
              hover
              borderless
              show-empty
              :items="rows"
              :fields="fields"
              @row-clicked="editFakeUser"
          >
            <template #cell(id)="{ item }">
              <button type="button" class="tl-id" @click.stop="copyId(item.id)"
                      v-b-tooltip.hover.right="'Скопировать ID'">
                <span>#{{ item.id }}</span>
                <i class="fa fa-copy"></i>
              </button>
            </template>

            <template #cell(username)="{ item }">
              <div class="tl-name">
                <span class="tl-name__logo">{{ initials(item.username) }}</span>
                <button type="button" class="tl-name__title" @click.stop="editFakeUser(item)" :title="item.username">
                  {{ item.username || '—' }}
                </button>
              </div>
            </template>

            <template #cell(profiles)="{ item }">
              <div v-if="item.x_profiles.length" class="tl-profiles">
                <span v-for="profile in item.x_profiles" :key="item.id + '-' + profile.slug"
                      class="tl-profile" :class="'tl-profile--' + profile.slug">
                  <b>{{ profile.label }}</b>
                  <span>{{ profile.nick || '—' }}</span>
                  <em v-if="profile.gameId">ID {{ profile.gameId }}</em>
                </span>
              </div>
              <span v-else class="tl-muted">игровых профилей нет</span>
            </template>

            <template #cell(action)="{ item }">
              <div class="tl-actions" @click.stop>
                <button type="button" class="tl-act" @click="editFakeUser(item)"
                        v-b-tooltip.hover.top="'Редактировать'">
                  <i class="fa fa-pencil"></i>
                </button>
                <button type="button" class="tl-act tl-act--danger" @click="deleteFakeUser(item.id)"
                        v-b-tooltip.hover.top="'Удалить'">
                  <i class="fa fa-trash-o"></i>
                </button>
              </div>
            </template>

            <template #empty>
              <div class="tl-empty tl-empty--inline">Ничего не найдено</div>
            </template>
          </b-table>
        </div>

        <!-- grid -->
        <div v-else class="tl-grid">
          <article v-for="item in rows" :key="'card' + item.id" class="tl-card" @click="editFakeUser(item)">
            <header class="tl-card__head">
              <span class="tl-card__logo">{{ initials(item.username) }}</span>
              <div class="tl-card__headtext">
                <button type="button" class="tl-card__title" @click.stop="editFakeUser(item)" :title="item.username">
                  {{ item.username || '—' }}
                </button>
                <div class="tl-card__sub">
                  <span class="tl-id tl-id--sm">#{{ item.id }}</span>
                </div>
              </div>
            </header>

            <div class="tl-card__rows">
              <div v-if="item.x_profiles.length" class="tl-profiles">
                <span v-for="profile in item.x_profiles" :key="item.id + '-c-' + profile.slug"
                      class="tl-profile" :class="'tl-profile--' + profile.slug">
                  <b>{{ profile.label }}</b>
                  <span>{{ profile.nick || '—' }}</span>
                  <em v-if="profile.gameId">ID {{ profile.gameId }}</em>
                </span>
              </div>
              <div v-else class="tl-muted">Игровых профилей нет</div>
            </div>

            <footer class="tl-card__foot" @click.stop>
              <button type="button" class="tl-act" @click="editFakeUser(item)" v-b-tooltip.hover.top="'Редактировать'">
                <i class="fa fa-pencil"></i>
              </button>
              <span class="tl-spacer"></span>
              <button type="button" class="tl-act tl-act--danger" @click="deleteFakeUser(item.id)"
                      v-b-tooltip.hover.top="'Удалить'">
                <i class="fa fa-trash-o"></i>
              </button>
            </footer>
          </article>
        </div>

        <div v-if="!isLoadingFakeUsers && rows.length" class="tl-foot">
          <div class="tl-foot__info">
            Показано <b>{{ rangeFrom }}–{{ rangeTo }}</b> из <b>{{ totalPagesOfFakeUsers || 0 }}</b>
            <span v-if="refine !== 'all'" class="tl-muted"> · отфильтровано на странице</span>
          </div>
          <b-pagination
              class="tl-pagination my-0"
              v-model="fakeUsersCurrentPage"
              :total-rows="totalPagesOfFakeUsers"
              :per-page="perPage"
              :limit="7"
              @change="goToPage"
          />
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import {PaginationHelperMixin} from "@/mixins/Helpers/PaginationHelperMixin";
import Modal from '@/components/modals/index.vue'
import {mapState} from "vuex";

const LS = {
  view: "fakeUsersListView",
  density: "fakeUsersListDensity",
};

/** Какие поля игрового профиля показываем для каждой дисциплины. */
const GAME_PROFILES = [
  {slug: 'pubg-mobile', key: 'pubg', label: 'PUBG', idField: 'pubg_id'},
  {slug: 'mlbb', key: 'mlbb', label: 'MLBB', idField: 'mlbb_id'},
];

export default {
  name: "FakeUsers",
  components: {Modal},
  mixins: [PaginationHelperMixin],
  data() {
    return {
      fakeUsersPageKey: 'pageNumberFakeUsers',
      perPage: 10,

      searchModel: '',
      searchApplied: '',
      searchTimer: null,

      refine: 'all',

      viewMode: 'table',
      density: 'cozy',

      fields: [
        {key: 'id', label: 'ID'},
        {key: 'username', label: 'Username', sortable: true},
        {key: 'profiles', label: 'Игровые профили'},
        {key: 'action', label: '', thClass: 'tl-th--act', tdClass: 'tl-td--act'},
      ],

      isLoadingFakeUsers: false,
      isShowDeleteFakeUserModal: false,
      isShowEditFakeUserModal: false,
      isShowCreateFakeUserModal: false,
      selectedFakeUserId: 0,
      fakeUsersCurrentPage: 1,
    }
  },
  computed: {
    ...mapState('moduleFakeUsers', ['fakeUsers', 'totalPagesOfFakeUsers']),

    refineLabel() {
      if (this.refine === 'pubg') return 'Только с PUBG';
      if (this.refine === 'mlbb') return 'Только с MLBB';
      if (this.refine === 'empty') return 'Без игровых профилей';
      return 'Игровой профиль';
    },

    hasAnyRefine() {
      return Boolean(this.searchApplied) || this.refine !== 'all';
    },

    /** Игровые профили разбираем один раз здесь — в шаблоне остаются готовые чипы. */
    rows() {
      const list = (this.fakeUsers || []).map(user => {
        const profiles = [];
        GAME_PROFILES.forEach(game => {
          const profile = (user.game_profiles || []).find(item => item.game && item.game.slug === game.slug);
          if (!profile) return;
          const fields = profile.fields || {};
          profiles.push({
            slug: game.key,
            label: game.label,
            nick: fields.nick || '',
            gameId: fields[game.idField] || '',
          });
        });
        return {...user, x_profiles: profiles};
      });

      if (this.refine === 'empty') return list.filter(user => !user.x_profiles.length);
      if (this.refine === 'pubg') return list.filter(user => user.x_profiles.some(p => p.slug === 'pubg'));
      if (this.refine === 'mlbb') return list.filter(user => user.x_profiles.some(p => p.slug === 'mlbb'));
      return list;
    },

    rangeFrom() {
      if (!this.totalPagesOfFakeUsers) return 0;
      return (this.fakeUsersCurrentPage - 1) * this.perPage + 1;
    },

    rangeTo() {
      return Math.min(this.fakeUsersCurrentPage * this.perPage, this.totalPagesOfFakeUsers || 0);
    },
  },
  watch: {
    searchModel(value) {
      if (this.searchTimer) clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        if (this.searchApplied === value.trim()) return;
        this.searchApplied = value.trim();
        this.fakeUsersCurrentPage = 1;
        this.fetchFakeUsers();
      }, 400);
    },

    // после закрытия модалки данные могли измениться — перечитываем страницу
    isShowCreateFakeUserModal(isOpen, wasOpen) {
      if (wasOpen && !isOpen) this.fetchFakeUsers();
    },
    isShowEditFakeUserModal(isOpen, wasOpen) {
      if (wasOpen && !isOpen) this.fetchFakeUsers();
    },
    isShowDeleteFakeUserModal(isOpen, wasOpen) {
      if (wasOpen && !isOpen) this.fetchFakeUsers();
    },
  },
  mounted() {
    this.restorePreferences();
    this.fakeUsersCurrentPage = Number(this.getCurrentPageNumber(this.fakeUsersPageKey)) || 1;
    this.fetchFakeUsers();
    document.addEventListener("keydown", this.onKeydown);
  },
  beforeDestroy() {
    document.removeEventListener("keydown", this.onKeydown);
    if (this.searchTimer) clearTimeout(this.searchTimer);
  },
  methods: {
    async fetchFakeUsers() {
      this.isLoadingFakeUsers = true;
      this.recordPageToStorage(this.fakeUsersCurrentPage, this.fakeUsersPageKey);
      try {
        await this.$store.dispatch('moduleFakeUsers/getFakeUsers', {
          page: this.fakeUsersCurrentPage,
          ...(this.searchApplied && {search: this.searchApplied}),
        });
      } catch (e) {
        this.$toasted.error("Не удалось загрузить фейковых пользователей", {position: 'top-right', duration: 3000});
      } finally {
        this.isLoadingFakeUsers = false;
      }
    },

    refresh() {
      this.fetchFakeUsers();
    },

    goToPage(pageNumber) {
      this.fakeUsersCurrentPage = pageNumber;
      this.$nextTick(this.fetchFakeUsers);
    },

    clearSearch() {
      this.searchModel = '';
      if (!this.searchApplied) return;
      this.searchApplied = '';
      this.fakeUsersCurrentPage = 1;
      this.fetchFakeUsers();
    },

    resetAll() {
      this.searchModel = '';
      this.searchApplied = '';
      this.refine = 'all';
      this.fakeUsersCurrentPage = 1;
      this.fetchFakeUsers();
    },

    createFakeUser() {
      this.isShowCreateFakeUserModal = true;
    },

    editFakeUser(item) {
      this.selectedFakeUserId = typeof item === 'object' ? item.id : item;
      this.isShowEditFakeUserModal = true;
    },

    deleteFakeUser(id) {
      this.selectedFakeUserId = id;
      this.isShowDeleteFakeUserModal = true;
    },

    copyId(id) {
      const done = () => this.$toasted.success(`ID ${id} скопирован`, {position: 'top-right', duration: 1500});
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(String(id)).then(done).catch(() => {
        });
        return;
      }
      const area = document.createElement("textarea");
      area.value = String(id);
      document.body.appendChild(area);
      area.select();
      try {
        document.execCommand("copy");
        done();
      } finally {
        document.body.removeChild(area);
      }
    },

    initials(name) {
      if (!name) return "?";
      return String(name).trim().slice(0, 2).toUpperCase();
    },

    restorePreferences() {
      try {
        const view = localStorage.getItem(LS.view);
        if (view === "grid" || view === "table") this.viewMode = view;

        const density = localStorage.getItem(LS.density);
        if (density === "compact" || density === "cozy") this.density = density;
      } catch (e) { /* повреждённые настройки — берём значения по умолчанию */
      }
    },

    setViewMode(mode) {
      this.viewMode = mode;
      localStorage.setItem(LS.view, mode);
    },

    toggleDensity() {
      this.density = this.density === "compact" ? "cozy" : "compact";
      localStorage.setItem(LS.density, this.density);
    },

    onKeydown(event) {
      const tag = (event.target && event.target.tagName) || "";
      const isTyping = /INPUT|TEXTAREA|SELECT/.test(tag) || (event.target && event.target.isContentEditable);

      if (event.key === "Escape") {
        if (isTyping) return;
        if (this.hasAnyRefine) this.resetAll();
        return;
      }

      if (isTyping || event.ctrlKey || event.metaKey || event.altKey) return;

      if (event.key === "/") {
        event.preventDefault();
        if (this.$refs.search) this.$refs.search.focus();
      }
    },
  },
}
</script>

<style scoped lang="scss">
@import "~@/assets/scss/admin-list.scss";

.tl-name__title,
.tl-card__title {
  border: 0;
  background: none;
  padding: 0;
  text-align: left;
  cursor: pointer;
}

.tl-name__logo,
.tl-card__logo {
  font-weight: 700;
  font-size: 12px;
}

.tl-profiles {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.tl-profile {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  padding: 3px 9px;
  border-radius: 8px;
  background: var(--tl-soft);
  border: 1px solid var(--tl-border);
  font-size: 11.5px;

  b {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: .04em;
    color: var(--tl-muted);
  }

  span {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    color: var(--tl-text);
  }

  em {
    font-style: normal;
    font-size: 10.5px;
    color: var(--tl-muted);
  }

  &--pubg b { color: #d98b0a; }
  &--mlbb b { color: #3aa1e0; }
}
</style>
